import { masterIdFromRef, masterNameFromRef } from "../../../uploads/productDraftApi";
import { studioSn } from "../list/ProductItems";
import { isSareeLikeProduct } from "../productSareeFields";

/** How one row of the in-place editor behaves. Keys are the product's own field names (what PATCH /fields accepts). */
export type FieldKind = "text" | "textarea" | "tags" | "mark";
export type FieldSpec = {
  key: string;
  label: string;
  kind: FieldKind;
  placeholder?: string;
  inputMode?: "text" | "numeric" | "decimal";
  maxLength?: number;
};
export type FieldGroup = { title: string; sareeOnly?: boolean; fields: FieldSpec[] };

export const NAME_FIELD: FieldSpec = { key: "name", label: "Product name", kind: "text", maxLength: 500 };

export const BASIC_FIELDS: FieldSpec[] = [
  { key: "color", label: "Colour", kind: "text", placeholder: "e.g. Green border", maxLength: 500 },
  { key: "description", label: "Description", kind: "textarea", placeholder: "How it was made, what makes it special", maxLength: 2000 },
  { key: "tags", label: "Tags", kind: "tags", placeholder: "katan, meena, festive" },
];

export const DETAIL_GROUPS: FieldGroup[] = [
  {
    title: "Saree",
    sareeOnly: true,
    fields: [
      { key: "typeOfSareeByDesign", label: "Design type", kind: "text", placeholder: "Kadhua butidar, jangla…" },
      { key: "zari", label: "Zari used", kind: "text", placeholder: "Real zari, tested zari…" },
      { key: "purity", label: "Zari purity", kind: "text" },
      { key: "zariWeight", label: "Zari weight", kind: "text", inputMode: "decimal" },
      { key: "sareeWeight", label: "Weight (grams)", kind: "text", inputMode: "decimal" },
      { key: "yarnType", label: "Yarn", kind: "text", placeholder: "Katan silk, mulberry…" },
    ],
  },
  {
    title: "Origin",
    fields: [
      { key: "weavingCenter", label: "Weaving centre", kind: "text", placeholder: "Village or town" },
      { key: "weaver", label: "Name of weaver", kind: "text" },
      { key: "loomNum", label: "Loom number", kind: "text" },
      { key: "productionTimeInMonths", label: "Time taken (months)", kind: "text", inputMode: "decimal" },
      { key: "workingHours", label: "Working hours", kind: "text", inputMode: "decimal" },
    ],
  },
  {
    title: "Marks",
    fields: [
      // Silk and Handloom are mark *numbers* (the certificate prints them); only Made in India is yes/no.
      { key: "silk", label: "Silk Mark number", kind: "text", placeholder: "e.g. SM-12345" },
      { key: "handloom", label: "Handloom Mark number", kind: "text" },
      { key: "madeIn", label: "Made in India", kind: "mark" },
    ],
  },
  {
    title: "Numbers",
    fields: [
      { key: "designNum", label: "Design number", kind: "text" },
      { key: "brand", label: "Brand", kind: "text" },
      { key: "hsnCode", label: "HSN code", kind: "text", inputMode: "numeric" },
    ],
  },
];

/** Groups that apply to this piece (the saree group only for saree-like categories). */
export const detailGroupsFor = (product: any): FieldGroup[] => {
  const saree = isSareeLikeProduct({
    type: product?.type,
    category: product?.category,
    subCategory: product?.subcategory,
    artform: product?.artform,
  });
  return DETAIL_GROUPS.filter((g) => !g.sareeOnly || saree);
};

export const isMarkOn = (value: unknown): boolean => value === true || value === "true" || value === 1 || value === "1";

/** The editable text for a field: tags join with ", ", marks are "true"/"false", everything else is its string. */
export const rawValue = (product: any, key: string): string => {
  const v = product?.[key];
  if (v == null) return "";
  if (Array.isArray(v)) return v.filter((x) => typeof x === "string" && x.trim()).join(", ");
  return String(v);
};

/** What the row shows at rest; "" means the row offers "+ Add". */
export const displayValue = (product: any, spec: FieldSpec): string => {
  if (spec.kind === "mark") return isMarkOn(product?.[spec.key]) ? "Yes" : "";
  return rawValue(product, spec.key).trim();
};

/** The body PATCH /fields gets for one row. Tags become a list; a mark is the string the backend already stores. */
export const patchBodyFor = (spec: FieldSpec, text: string): Record<string, unknown> => {
  if (spec.kind === "tags") {
    const tags = text.split(/[,\n]/).map((t) => t.trim()).filter(Boolean);
    return { [spec.key]: Array.from(new Set(tags)) };
  }
  if (spec.kind === "mark") return { [spec.key]: text === "true" ? "true" : "false" };
  return { [spec.key]: text.trim() };
};

const MASTER_KEYS = new Set(["artform", "type", "category", "subcategory", "weavetype"]);
const same = (key: string, a: unknown, b: unknown): boolean => {
  const n = (v: unknown) =>
    MASTER_KEYS.has(key) ? masterIdFromRef(v) : Array.isArray(v) ? JSON.stringify(v) : v == null ? "" : String(v).trim();
  return n(a) === n(b);
};

/** True when the saved draft holds a different value than the live piece for this key. */
export const isChanged = (raw: any, key: string): boolean =>
  Boolean(raw?.hasDraftChanges && raw?.draft && typeof raw.draft === "object" && key in raw.draft && !same(key, raw.draft[key], raw[key]));

export const filledCount = (product: any, fields: FieldSpec[]): { filled: number; total: number } => ({
  filled: fields.filter((f) => displayValue(product, f) !== "").length,
  total: fields.length,
});

export const categoryPathText = (product: any): string =>
  [product?.artform, product?.type, product?.category, product?.subcategory, product?.weavetype]
    .map((r) => masterNameFromRef(r))
    .filter(Boolean)
    .join(" › ");

/** Studio SN first ("#105"), the long id only when there is none: the list card's rule. */
export const pieceSn = (product: any): string => studioSn(product);

/** Merges a successful PATCH into the product the page holds, so the row updates without a refetch. */
export const applyPatchResult = (raw: any, body: Record<string, unknown>, result: { saved?: string; fields?: Record<string, unknown>; draftRevision?: number }): any => {
  const fields = result?.fields && typeof result.fields === "object" ? result.fields : body;
  if (result?.saved === "draft") {
    return {
      ...raw,
      hasDraftChanges: true,
      draftRevision: result.draftRevision ?? raw?.draftRevision,
      draft: { ...(raw?.draft && typeof raw.draft === "object" ? raw.draft : {}), ...fields },
    };
  }
  const next = { ...raw, ...fields };
  if (raw?.draft && typeof raw.draft === "object") {
    const draft = { ...raw.draft };
    Object.keys(fields).forEach((k) => {
      if (k in draft) draft[k] = fields[k];
    });
    next.draft = draft;
  }
  return next;
};
